/**
 * Content Detail/Edit Page
 * Adapted from Next.js: useRouter/useParams → react-router-dom
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StatusBadge } from "@/components/shared";
import { trpc } from "@/lib/trpc";

interface EditedContent {
  title: string;
  caption: string;
}

export function ContentDetailPage() {
  const navigate = useNavigate();
  const { id: postId } = useParams<{ id: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<EditedContent>({
    title: "",
    caption: "",
  });
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const {
    data: post,
    isLoading,
    error,
    refetch,
  } = trpc.posts.getById.useQuery({ id: postId! }, { enabled: !!postId });

  const updateMutation = trpc.posts.update.useMutation({
    onSuccess: () => {
      setToast({ type: "success", message: "Post updated!" });
      setIsEditing(false);
      refetch();
    },
    onError: (err) => {
      setToast({ type: "error", message: err.message });
    },
  });
  const approveMutation = trpc.posts.approve.useMutation({
    onSuccess: () => {
      setToast({ type: "success", message: "Post approved!" });
      setTimeout(() => navigate("/content"), 1500);
    },
    onError: (err) => {
      setToast({ type: "error", message: err.message });
    },
  });
  const rejectMutation = trpc.posts.reject.useMutation({
    onSuccess: () => {
      setToast({ type: "success", message: "Post rejected" });
      setTimeout(() => navigate("/content"), 1500);
    },
    onError: (err) => {
      setToast({ type: "error", message: err.message });
    },
  });

  useEffect(() => {
    if (post)
      setEditedContent({ title: post.title, caption: post.caption || "" });
  }, [post]);

  const handleSave = () => {
    updateMutation.mutate({
      id: postId!,
      title: editedContent.title,
      caption: editedContent.caption,
    });
  };
  const handleApprove = () => {
    approveMutation.mutate({ id: postId!, notes: approvalNotes || undefined });
    setShowApproveDialog(false);
  };
  const handleReject = () => {
    if (!rejectReason.trim()) {
      setToast({ type: "error", message: "Please provide a reason" });
      return;
    }
    rejectMutation.mutate({ id: postId!, reason: rejectReason });
    setShowRejectDialog(false);
  };

  const canEdit = post?.status === "draft";
  const canApprove = post?.status === "pending_review";

  if (isLoading)
    return (
      <div className="min-h-screen bg-bg-primary p-6">
        <div className="mx-auto max-w-4xl animate-pulse space-y-6">
          <div className="h-8 bg-bg-elevated rounded w-1/3" />
          <div className="rounded-lg border border-border-default bg-bg-secondary p-6 space-y-4">
            <div className="h-6 bg-bg-elevated rounded w-1/4" />
            <div className="h-32 bg-bg-elevated rounded w-full" />
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen bg-bg-primary p-6">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-red-500/50 bg-red-950/20 p-6">
            <h3 className="text-lg font-medium text-red-400">
              {error.data?.code === "NOT_FOUND"
                ? "Post Not Found"
                : "Failed to Load Post"}
            </h3>
            <p className="mt-1 text-sm text-red-300/80">{error.message}</p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => navigate("/content")}
                className="text-sm text-red-400 hover:bg-red-950/30 px-4 py-2 rounded-md"
              >
                Back to Content
              </button>
              <button
                onClick={() => refetch()}
                className="text-sm text-red-400 hover:bg-red-950/30 px-4 py-2 rounded-md"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  if (!post) return null;

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate("/content")}
                className="text-text-secondary hover:text-text-primary"
                aria-label="Back to content queue"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-text-primary">
                {isEditing ? "Edit Content" : "Content Details"}
              </h1>
              <StatusBadge status={post.status} />
            </div>
          </div>
          <div className="flex gap-3">
            {canEdit && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="rounded-md border border-border-default bg-bg-secondary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-tertiary transition-colors"
              >
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="rounded-md border border-border-default bg-bg-secondary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-tertiary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 transition-colors"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6">
          <div className="rounded-lg border border-border-default bg-bg-secondary p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Content
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-text-secondary mb-2"
                >
                  Title
                </label>
                {isEditing ? (
                  <input
                    id="title"
                    type="text"
                    value={editedContent.title}
                    onChange={(e) =>
                      setEditedContent({
                        ...editedContent,
                        title: e.target.value,
                      })
                    }
                    className="w-full rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-text-primary"
                  />
                ) : (
                  <p className="text-text-primary">{post.title}</p>
                )}
              </div>
              <div>
                <label
                  htmlFor="caption"
                  className="block text-sm font-medium text-text-secondary mb-2"
                >
                  Content
                </label>
                {isEditing ? (
                  <textarea
                    id="caption"
                    value={editedContent.caption}
                    onChange={(e) =>
                      setEditedContent({
                        ...editedContent,
                        caption: e.target.value,
                      })
                    }
                    rows={8}
                    className="w-full rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-text-primary"
                  />
                ) : (
                  <p className="text-text-primary whitespace-pre-wrap">
                    {post.caption || "(No content)"}
                  </p>
                )}
              </div>
              <div>
                <p className="block text-sm font-medium text-text-secondary mb-2">
                  Platforms
                </p>
                <div className="flex flex-wrap gap-2">
                  {post.platforms.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center rounded-md bg-bg-elevated px-3 py-1 text-sm font-medium text-text-secondary border border-border-subtle capitalize"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {canApprove && (
            <div className="rounded-lg border border-border-default bg-bg-secondary p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                Approval Actions
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApproveDialog(true)}
                  className="flex-1 rounded-md bg-success-500 px-4 py-2 text-sm font-medium text-white hover:bg-success-600 transition-colors"
                >
                  Approve
                </button>
                <button
                  onClick={() => setShowRejectDialog(true)}
                  className="flex-1 rounded-md bg-error-500 px-4 py-2 text-sm font-medium text-white hover:bg-error-600 transition-colors"
                >
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>

        {toast && (
          <div className="fixed bottom-4 right-4 z-50">
            <div
              className={`rounded-lg px-4 py-3 shadow-lg ${toast.type === "success" ? "border border-primary-500/50 bg-primary-950/90 text-primary-400" : "border border-red-500/50 bg-red-950/90 text-red-400"}`}
            >
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium">{toast.message}</p>
                <button onClick={() => setToast(null)} className="ml-2">
                  X
                </button>
              </div>
            </div>
          </div>
        )}

        {showApproveDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-bg-secondary border border-border-default rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Approve Content
              </h3>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border-default bg-bg-tertiary px-3 py-2 text-sm text-text-primary mb-4"
                placeholder="Notes (optional)"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowApproveDialog(false)}
                  className="rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="rounded-md bg-success-500 px-4 py-2 text-sm font-medium text-white"
                >
                  {approveMutation.isPending ? "Approving..." : "Approve"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showRejectDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-bg-secondary border border-border-default rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Reject Content
              </h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border-default bg-bg-tertiary px-3 py-2 text-sm text-text-primary mb-4"
                placeholder="Reason (required)"
                required
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRejectDialog(false)}
                  className="rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending || !rejectReason.trim()}
                  className="rounded-md bg-error-500 px-4 py-2 text-sm font-medium text-white"
                >
                  {rejectMutation.isPending ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
